import {AppInitializeService} from './app-initialize-service';
import {inject, Injectable} from './di';
import {Logger} from './logger';
import {getJson} from './utils/get-json';
import {LegacyLogService} from './logger/legacy-log-service';
import {StructService} from '@core/struct/struct-service';

@Injectable()
export class CustomizationService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly legacyLogService = inject(LegacyLogService);
  private readonly structService = inject(StructService);

  public _config: CustomisationConfig = {
    // default values
    main_color: '#FFFFFF',
    main_background: '#000000',
  };

  constructor() {
    window.Customization = this;
  }

  public async init(): Promise<boolean> {
    try {
      if (!document.getElementById('customization_css_new')) {
        let customizationCssEl = $('#customization_css');
        if (!customizationCssEl.length) {
          this.logger.warn('[CustomizationService] #customization_css element was not found!');

          // re-create customization_css tag
          customizationCssEl = $('<style id="customization_css" type="text/css"></style>');
          $(document.head).append(customizationCssEl);
        }
        customizationCssEl.after('<style id="customization_css_new"></style>');
      }

      $(document.body).addClass('main_color main_background');

      try {
        this._config = await getJson<CustomisationConfig>(`${this.structService.contentUrl}customization.json?r=${Math.random()}`);
        this.legacyLogService.add('Customization: inited');
      } catch (err) {
        this.logger.error(`[CustomizationService] Unable to load customization.json!`, err);
        this.legacyLogService.add('Customization: no customization');
      }

      let splash_src = 'url("tv/splash.png")';
      const splashImage = this.get('splash_image');
      if (splashImage) {
        splash_src = `url("${splashImage}")`;
      } else {
        const splash_css = $('#splashscreen').css('background-image');
        if (splash_css && splash_css != 'none') {
          splash_src = splash_css;
        }
      }
      if (splash_src) {
        $('#customization_css_new').append('#splashscreen { background-image: ' + splash_src + '; }');
      }

      const rawCss = this.get('raw_css');
      if (rawCss) {
        $('#customization_css_new').append(rawCss);
      }
    } catch (error) {
      this.logger.critical('[CustomizationService] Unable to initialize', error);
      return false;
    }

    return true;
  }

  /**
   * Get customisation param.
   * @param param param name
   */
  public get(param: keyof CustomisationConfig | `${string}_color` | `${string}_background`): string | undefined {
    if (this._config[param as keyof CustomisationConfig]) {
      return this._config[param as keyof CustomisationConfig]!;
    } else if (param.endsWith('_color')) {
      return this._config.main_color;
    } else if (param.endsWith('_background')) {
      return this._config.main_background;
    }
  }

  /**
   * Check if customisation has exact parameter, excluding fallbacks.
   * @param param parameter name
   */
  public has(param: keyof CustomisationConfig): boolean {
    return !!this._config[param];
  }

  public add(param: keyof CustomisationConfig, value?: string) {
    try {
      if (!value) {
        value = this.get(param);
      }

      if (!value) {
        this.logger.error(`[CustomizationService.add] empty customization param ${param}`);
        this.legacyLogService.add(`Customization: empty customization param ${param}`);
        return;
      }

      const styleMatch = param.match('(.+)_(.+?)$');
      let style: string;
      if (styleMatch) {
        style = styleMatch[2];
      } else {
        this.legacyLogService.add('Customization: incorrect customization param');
        this.logger.error(`[Customization.add] incorrect customization param: ${param}`);
        return;
      }

      if (!value.match('^#')) {
        value = 'url("' + value + '")';
      }
      $('#customization_css_new').append(`.${param} { ${style}: ${value}}`);
    } catch (err) {
      this.logger.error(`[CustomizationService.add] Unexpected error while adding customization param ${param}`, err);
    }
  }

  private set(param: keyof CustomisationConfig, value: string) {
    this._config[param] = value;
  }
}

declare global {
  interface Window {
    Customization: CustomizationService;
  }
  const Customization: CustomizationService;
}

interface CustomisationConfig {
  main_color: string;
  main_background: string;
  menu_color?: string;
  menu_background?: string;
  menu_tile_background?: string;
  menu_image_color?: string;
  button_color?: string;
  button_background?: string;
  channels_color?: string;
  cursor_color?: string;
  cursor_color2?: string;
  logo_width?: string;
  raw_css?: string;
  logo_url?: string;
  font_primary?: string;
  font_secondary?: string;
  splash_image?: string;
  channels_background?: string;
}
